import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ExternalLink, Save, Sparkles, CheckCircle2, XCircle, Clock, Radio, Zap, Bell, MousePointerClick, DollarSign, Network, Rocket } from "lucide-react";

const NETWORK_LABEL = {
  impact: "Impact.com", partnerstack: "PartnerStack", cj: "CJ Affiliate", shareasale: "ShareASale",
  awin: "Awin", rakuten: "Rakuten", clickbank: "ClickBank", flexoffers: "FlexOffers",
  "in-house": "In-house / direct", other: "Other",
};
const NETWORK_PATTERNS = [
  ["impact", /\bimpact(\.com)?\b/i], ["partnerstack", /\bpartner\s?stack\b/i],
  ["cj", /\b(cj|commission\s?junction)\b/i], ["shareasale", /share\s?a\s?sale/i],
  ["awin", /\bawin\b/i], ["rakuten", /\brakuten\b/i], ["clickbank", /\bclick\s?bank\b/i],
  ["flexoffers", /\bflex\s?offers\b/i], ["in-house", /(in.?house|direct|proprietary|own\s+program)/i],
];
const classifyNetwork = (guess) => {
  const g = (guess || "").toLowerCase();
  for (const [key, re] of NETWORK_PATTERNS) if (re.test(g)) return key;
  return "other";
};

const STATUS_META = {
  not_applied: { label: "Not applied", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Clock },
  applied: { label: "Applied", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle },
  live: { label: "Live", cls: "bg-emerald-600 text-white border-emerald-700", Icon: Radio },
};

const STATUS_OPTIONS = ["not_applied", "applied", "approved", "rejected", "live"];

const daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

export function AdminOfficialLinks({ client, API }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState(null);
  const [submittingSlug, setSubmittingSlug] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showClickValues, setShowClickValues] = useState(false);
  const [goingLive, setGoingLive] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    client().get(`${API}/admin/official-links`).then((r) => {
      setRows(r.data.programs);
      setCounts(r.data.counts || {});
      setShowClickValues(!!r.data.show_click_values);
      const nextDrafts = {};
      r.data.programs.forEach((p) => { nextDrafts[p.slug] = { affiliate_url: p.affiliate_url, status: p.status, notes: p.notes }; });
      setDrafts(nextDrafts);
    }).catch(() => toast.error("Failed to load official links")).finally(() => setLoading(false));
  }, [client, API]);

  useEffect(() => { load(); }, [load]);

  const bulkSubmit = async () => {
    if (!window.confirm("Auto-submit every program that has a generated application pack and is still 'Not applied'?\n\nThis runs in the background — many will need CAPTCHA / manual finish.")) return;
    setBulkLoading(true);
    try {
      const { data } = await client().post(`${API}/admin/official-links/submit-all-pending`);
      if (data.status === "nothing_pending") toast("Nothing to submit — all packed programs are already applied or beyond.");
      else toast.success(`Started auto-submit for ${data.count} programs. Check back in a few minutes.`);
      setTimeout(load, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk submit failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const setDraft = (slug, patch) => setDrafts((d) => ({ ...d, [slug]: { ...(d[slug] || {}), ...patch } }));

  const save = async (slug) => {
    setSavingSlug(slug);
    try {
      const payload = { ...(drafts[slug] || {}) };
      const row = rows.find((r) => r.slug === slug);
      if (payload.status === "live" && row?.status !== "live") {
        payload.notify_ambassadors = window.confirm(
          `"${row?.program_name}" is going LIVE.\n\nEmail every ambassador their personal share link for this program right now?\n\nOK = send the announcement blast\nCancel = go live silently`
        );
      }
      const { data } = await client().put(`${API}/admin/official-links/${slug}`, payload);
      if (data.ambassadors_notified) toast.success("Saved — live link synced & ambassador announcement emails are sending");
      else toast.success(data.synced_to_master_link ? "Saved — live link auto-synced to /go/" : "Saved");
      setRows((rs) => rs.map((r) => r.slug === slug ? { ...r, ...payload, updated_at: new Date().toISOString() } : r));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSlug(null);
    }
  };

  const autoSubmit = async (slug) => {
    if (!window.confirm("Start automated form submission for this program?\n\nHeads-up: many affiliate networks use CAPTCHA / bot protection. The system will fill what it can and screenshot the outcome — you may still need to finish manually.")) return;
    setSubmittingSlug(slug);
    try {
      await client().post(`${API}/admin/official-links/${slug}/auto-submit`);
      toast.success("Submission started. Polling for result…");
      const poll = setInterval(async () => {
        try {
          const { data } = await client().get(`${API}/admin/official-links/${slug}/submission-status`);
          if (!data.running && data.result) {
            clearInterval(poll);
            setSubmittingSlug(null);
            const st = data.result.status;
            const msg = data.result.reason || st;
            if (st === "submitted") toast.success(`Submitted: ${msg}`);
            else if (st === "submitted_unconfirmed") toast(`Submitted (unconfirmed): ${msg}`);
            else toast.warning(`${st}: ${msg}`, { duration: 8000 });
            load();
          }
        } catch (err) { console.error("Polling submission-status failed:", err?.response?.status || err?.message); }
      }, 3000);
      setTimeout(() => { clearInterval(poll); if (submittingSlug === slug) setSubmittingSlug(null); }, 120000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start submission");
      setSubmittingSlug(null);
    }
  };

  const filtered = rows.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (search && !`${r.program_name} ${r.niche_name}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group non-live, non-rejected programs by detected network for one-tap "Go live"
  const networkGroups = useMemo(() => {
    const g = {};
    for (const r of rows) {
      if (r.status === "live" || r.status === "rejected") continue;
      const key = classifyNetwork(r.network_guess);
      if (key === "other" || key === "in-house") continue; // only real networks get a one-tap push
      (g[key] = g[key] || []).push(r);
    }
    return g;
  }, [rows]);

  const bulkGoLive = async (netKey) => {
    const list = networkGroups[netKey] || [];
    const label = NETWORK_LABEL[netKey] || netKey;
    if (!list.length) return;
    if (!window.confirm(`Mark all ${list.length} ${label} program(s) LIVE now?\n\nThey'll appear on the public Official Links directory instantly. Programs without a pasted affiliate URL will fall back to their signup URL.`)) return;
    const notify = window.confirm("Also email every ambassador their new share links for these programs?\n\nOK = send the announcement\nCancel = go live silently");
    setGoingLive(netKey);
    try {
      const slugs = list.map((r) => r.slug);
      const { data } = await client().post(`${API}/admin/official-links/bulk-status`, { slugs, status: "live", use_join_url_fallback: true, notify_ambassadors: notify });
      toast.success(`${data.updated} ${label} program(s) live${data.synced ? ` · ${data.synced} synced to /go/` : ""}${data.notified ? ` · ${data.notified} announced` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Go-live failed");
    } finally {
      setGoingLive(null);
    }
  };

  const staleCount = rows.filter((r) => r.status === "applied" && daysSince(r.updated_at) >= 14).length;
  const networkKeys = Object.keys(networkGroups);

  return (
    <div data-testid="admin-official-links" className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-2xl tracking-tight mb-1">Official affiliate links</h2>
            <p className="text-sm text-slate-500 max-w-2xl">Paste each approved affiliate URL here. When you mark a program as <b>Approved</b> or <b>Live</b>, the URL auto-syncs to <code className="bg-slate-100 px-1 rounded">/go/&#123;handle&#125;/{'{'}slug{'}'}</code> so ambassador clicks land on the real link.</p>
          </div>
          <button onClick={bulkSubmit} disabled={bulkLoading} data-testid="ol-bulk-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors disabled:opacity-60">
            {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Submit all pending
          </button>
        </div>
        {staleCount > 0 && (
          <div data-testid="ol-followup-banner" className="mt-4 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <Bell className="w-4 h-4 text-amber-700 flex-shrink-0" />
            <p className="text-sm text-amber-900">
              <b>{staleCount}</b> application{staleCount !== 1 ? "s" : ""} pending for 14+ days — time to follow up.
            </p>
            <button onClick={() => setFilter("applied")} className="ml-auto text-xs font-semibold text-amber-800 hover:underline">Show applied →</button>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          {["all", ...STATUS_OPTIONS].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
              className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-50"}`}>
              {s === "all" ? `All (${rows.length})` : `${STATUS_META[s].label} (${counts[s] || 0})`}
            </button>
          ))}
        </div>
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search programs…" data-testid="official-links-search"
          className="mt-4 w-full sm:max-w-sm px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
      </div>

      {networkKeys.length > 0 && (
        <div data-testid="ol-golive-panel" className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-1">
            <Rocket className="w-4 h-4 text-emerald-600" />
            <h3 className="font-heading text-lg">Approved a network? Push it live in one tap</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4 max-w-2xl">Once a network approves you, flip all its programs to <b>Live</b> at once — they appear on the public Official Links directory instantly. Missing affiliate URLs fall back to the signup URL (paste the tracked link later).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {networkKeys.map((k) => (
              <div key={k} data-testid={`ol-golive-${k}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <span className="inline-flex items-center gap-1.5 text-slate-700 font-semibold text-sm">
                  <Network className="w-3.5 h-3.5 text-indigo-600" /> {NETWORK_LABEL[k]}
                </span>
                <span className="text-xs text-slate-500">{networkGroups[k].length} pending</span>
                <button onClick={() => bulkGoLive(k)} disabled={goingLive === k}
                  data-testid={`ol-golive-btn-${k}`}
                  className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors disabled:opacity-60">
                  {goingLive === k ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Radio className="w-3.5 h-3.5" />} Go live
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => {
            const d = drafts[row.slug] || {};
            const meta = STATUS_META[d.status || "not_applied"];
            const dirty = d.affiliate_url !== row.affiliate_url || d.status !== row.status || d.notes !== row.notes;
            const sub = row.submission_result;
            const days = daysSince(row.updated_at);
            const stale = row.status === "applied" && days !== null && days >= 14;
            return (
              <div key={row.slug} data-testid={`ol-row-${row.slug}`} className={`bg-white border rounded-lg p-5 ${stale ? "border-amber-300" : "border-slate-200"}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-slate-900">{row.program_name}</h3>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.cls}`}>
                        <meta.Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      {stale && (
                        <span data-testid={`ol-stale-${row.slug}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                          <Bell className="w-3 h-3" /> Follow up · {days}d
                        </span>
                      )}
                      <span className="text-xs text-slate-400">{row.niche_name} · {row.commission}</span>
                      {showClickValues && (
                        <span data-testid={`ol-clickvalue-${row.slug}`} className="inline-flex items-center gap-2 text-[11px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5">
                          <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3 text-indigo-600" /> {row.clicks || 0} clicks</span>
                          <span className="inline-flex items-center gap-1"><DollarSign className="w-3 h-3 text-emerald-600" /> ${(row.revenue || 0).toFixed(2)} earned</span>
                          <span className="text-slate-500">${(row.rpc || 0).toFixed(2)}/click</span>
                        </span>
                      )}
                    </div>
                    {row.signup_url && (
                      <a href={row.signup_url} target="_blank" rel="noopener noreferrer" data-testid={`ol-signup-${row.slug}`}
                        className="inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:underline mt-1">
                        Signup page <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => autoSubmit(row.slug)} disabled={submittingSlug === row.slug || !row.signup_url}
                      data-testid={`ol-autosubmit-${row.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-500 transition-colors duration-200 disabled:opacity-60">
                      {submittingSlug === row.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Auto-submit
                    </button>
                    <button onClick={() => save(row.slug)} disabled={!dirty || savingSlug === row.slug}
                      data-testid={`ol-save-${row.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors duration-200 disabled:opacity-40">
                      {savingSlug === row.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr,180px] gap-3">
                  <input type="url" placeholder="Paste your approved affiliate URL (e.g. https://impact.com/?ref=…)"
                    value={d.affiliate_url || ""} onChange={(e) => setDraft(row.slug, { affiliate_url: e.target.value })}
                    data-testid={`ol-url-${row.slug}`}
                    className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  <select value={d.status || "not_applied"} onChange={(e) => setDraft(row.slug, { status: e.target.value })}
                    data-testid={`ol-status-${row.slug}`}
                    className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                  </select>
                </div>
                <input type="text" placeholder="Notes (optional)" value={d.notes || ""}
                  onChange={(e) => setDraft(row.slug, { notes: e.target.value })}
                  data-testid={`ol-notes-${row.slug}`}
                  className="mt-3 w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                {sub && (
                  <div className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="font-semibold text-slate-700 mb-1">Last submission: <span className="font-mono text-slate-500">{sub.status}</span></p>
                    <p className="text-slate-600">{sub.reason || "—"}</p>
                    {typeof sub.fields_filled_count === "number" && <p className="text-slate-400 mt-1">Filled {sub.fields_filled_count} fields · CAPTCHA {sub.captcha_detected ? "detected" : "none"}</p>}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && <p className="text-center text-sm text-slate-400 py-12">No programs match.</p>}
        </div>
      )}
    </div>
  );
}
